// src/components/CartContext.jsx
import React, { createContext, useContext, useMemo, useState } from "react";

/* Key builder keeps items unique by id + variant (if any) */
const keyOf = (item) => `${item?.id ?? ""}-${item?.variant ?? "default"}`;

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]); // each item: {id, name, price, qty, img, variant?}

  const addToCart = (item) => {
    if (!item || item.price == null) return;
    const qty = Math.max(1, parseInt(item.qty ?? 1, 10) || 1);
    const k = keyOf(item);
    setCart((prev) => {
      const idx = prev.findIndex((p) => keyOf(p) === k);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: (copy[idx].qty || 1) + qty };
        return copy;
      }
      return [...prev, { ...item, qty }];
    });
  };

  const updateQty = (item, qty) => {
    const k = keyOf(item);
    const n = Math.max(1, parseInt(qty, 10) || 1);
    setCart((prev) => {
      const idx = prev.findIndex((p) => keyOf(p) === k);
      if (idx < 0) return prev;
      const copy = [...prev];
      copy[idx] = { ...copy[idx], qty: n };
      return copy;
    });
  };

  const removeFromCart = (item) => {
    const k = keyOf(item);
    setCart((prev) => prev.filter((p) => keyOf(p) !== k));
  };

  const clearCart = () => setCart([]);

  /* ---- Backward-compatibility aliases ----
     Many components use cart.addItem/removeItem. Keep both APIs working. */
  const value = useMemo(
    () => ({
      cart,
      addToCart,
      removeFromCart,
      clearCart,
      updateQty,
      // aliases (so existing code that calls addItem/removeItem won't crash)
      addItem: addToCart,
      removeItem: removeFromCart,
    }),
    [cart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
